import { availableStatViews } from '@digital-twins/deck-ui/plots/_shared'
import { divergingLikertStats, quickStudyDivergingLikertAdapter } from '@digital-twins/deck-ui/plots/diverging-likert'
import { quickStudyHeatGridAdapter, quickStudyScorecardAdapter } from '@digital-twins/deck-ui/plots/heat-grid'

import { Result } from './quickStudyTypes'

import { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * The stat views a quick-study card's stamped plot offers (W6-QS), and the
 * payload's own words for them. The card's metric switch reads this, so it
 * offers only a view the plot can draw and names it as the plot does: a 10 or
 * 11 point scale reads "Top 3", never "Top-2-box %". Null when the result
 * carries no stamped rating plot (stored results keep the legacy switch).
 */

export interface StampedStatChoice {
  views: Array<PlotStatView>
  labels: {
    mean: string | null
    topBoxes: string | null
    nps: string | null
  }
}

function filledTopN(template: string | null | undefined, size: number | null | undefined): string | null {
  if (!template) {
    return null
  }
  return typeof size === 'number' ? template.replace('{n}', String(size)) : null
}

export function stampedStatChoice(
  result: Pick<Result, 'diverging_likert_plot' | 'heat_grid_plot' | 'scorecard_plot'>,
): StampedStatChoice | null {
  if (quickStudyDivergingLikertAdapter.accepts(result)) {
    const { data } = quickStudyDivergingLikertAdapter.read(result)
    const stats = divergingLikertStats(data)
    return {
      views: stats ? availableStatViews(stats) : ['mean'],
      labels: {
        mean: data.labels.stat_tab_average ?? null,
        topBoxes: filledTopN(data.labels.stat_tab_top_n, stats?.boxSize),
        nps: data.labels.stat_tab_nps ?? null,
      },
    }
  }
  // SC: a scorecard's own plot first (its views are the ones drawn).
  const heatGrid = quickStudyScorecardAdapter.accepts(result)
    ? quickStudyScorecardAdapter.read(result)
    : quickStudyHeatGridAdapter.accepts(result)
      ? quickStudyHeatGridAdapter.read(result)
      : null
  if (heatGrid) {
    const { data } = heatGrid
    return {
      views: data.views ? [...data.views] : ['mean'],
      labels: {
        mean: data.statLabels?.average ?? null,
        topBoxes: data.statLabels?.topBoxes ?? null,
        nps: data.statLabels?.nps ?? null,
      },
    }
  }
  return null
}

/** Whether the stamped plot offers `view` (the card's switch may hold it only then). */
export function stampedPlotOffers(
  result: Pick<Result, 'diverging_likert_plot' | 'heat_grid_plot' | 'scorecard_plot'>,
  view: PlotStatView | null,
): boolean {
  const choice = stampedStatChoice(result)
  return view !== null && choice !== null && choice.views.includes(view)
}
